/*
 * Task API routes
 * Purpose: Expose the first persisted task domain through the shared data-route layer.
 * Implementation notes: Routes return semantic task status/source values, aggregate multi-assignee display data, enforce creator-or-assignee access, and queue requested assignment email after task persistence.
 * Recent changes: Level-note source restrictions apply to task reads, mutations, assignees, and notification recipients.
 * Recent changes: A task derived from a level note keeps its source link so copied content cannot be reclassified through task editing.
 * Recent changes: Task create and update notifications personalize the shared assignment template from saved task and assignee data.
 */

import { z } from 'zod';
import { DataRoutes } from './types';

const taskStatusSchema = z.enum(['Open', 'Waiting', 'Done', 'Canceled']);
const taskPrioritySchema = z.enum(['High', 'Medium', 'Low']);
const taskSourceTypeSchema = z.enum(['Manual', 'Note']);

const taskSelect = `
  SELECT
    t.Id as id,
    t.Title as title,
    t.Description as description,
    t.Status as status,
    t.Priority as priority,
    CONVERT(VARCHAR(10), t.DueDate, 23) as dueDate,
    t.AccountId as accountId,
    a.Name as accountName,
    t.ContactId as contactId,
    c.Name as contactName,
    t.SourceNoteId as sourceNoteId,
    n.Title as sourceNoteTitle,
    t.SourceType as sourceType,
    t.CreatedByUserId as createdByUserId,
    creator.Name as createdByUserName,
    CONVERT(VARCHAR(33), t.CreatedAt, 127) as createdAt,
    CONVERT(VARCHAR(33), t.UpdatedAt, 127) as updatedAt,
    CONVERT(VARCHAR(33), t.CompletedAt, 127) as completedAt,
    CONVERT(VARCHAR(33), t.DeletedAt, 127) as deletedAt,
    t.DeletedByUserId as deletedByUserId,
    deleter.Name as deletedByUserName,
    COALESCE(assignees.AssigneeIds, '') as assigneeIds,
    COALESCE(assignees.AssigneeNames, '') as assigneeNames
  FROM dbo.Tasks t
  INNER JOIN dbo.Accounts a ON a.Id = t.AccountId
  LEFT JOIN dbo.Contacts c ON c.Id = t.ContactId
  LEFT JOIN dbo.Notes n ON n.Id = t.SourceNoteId
  LEFT JOIN dbo.Users creator ON creator.Id = t.CreatedByUserId
  LEFT JOIN dbo.Users deleter ON deleter.Id = t.DeletedByUserId
  OUTER APPLY (
    SELECT
      STUFF((
        SELECT ',' + CAST(ta.UserId AS VARCHAR(20))
        FROM dbo.TaskAssignees ta
        WHERE ta.TaskId = t.Id
        ORDER BY ta.UserId
        FOR XML PATH(''), TYPE
      ).value('.', 'NVARCHAR(MAX)'), 1, 1, '') as AssigneeIds,
      STUFF((
        SELECT ',' + u.Name
        FROM dbo.TaskAssignees ta
        INNER JOIN dbo.Users u ON u.Id = ta.UserId
        WHERE ta.TaskId = t.Id
        ORDER BY ta.UserId
        FOR XML PATH(''), TYPE
      ).value('.', 'NVARCHAR(MAX)'), 1, 1, '') as AssigneeNames
  ) assignees
`;

// Existing team-note task access is preserved; level-note creators retain access to their own derived content.
const taskSourceAccessPredicate = `(
  t.SourceNoteId IS NULL
  OR EXISTS (
    SELECT 1 FROM dbo.Notes SourceNote WITH (HOLDLOCK)
    WHERE SourceNote.Id = t.SourceNoteId
      AND (SourceNote.VisibilityMode = 'teams' OR EXISTS (
        SELECT 1 FROM dbo.fnVisibleNoteIds(dbo.fnGetContextUser()) VisibleSource
        WHERE VisibleSource.Id = SourceNote.Id
      ))
  )
)`;

const taskAssigneeLevelGuard = `
  IF EXISTS (
    SELECT 1 FROM dbo.Notes SourceNote WITH (HOLDLOCK)
    CROSS JOIN @assigneeIdsTable Assignee
    JOIN dbo.Users AssigneeUser WITH (HOLDLOCK) ON AssigneeUser.Id = Assignee.Id
    WHERE SourceNote.Id = @effectiveSourceNoteId
      AND SourceNote.VisibilityMode = 'levels'
      AND AssigneeUser.AccessLevel < SourceNote.MinimumLevel
      AND Assignee.Id <> SourceNote.CreatedBy
  ) THROW 99107, 'Task assignment denied: assignee does not meet the source note level.', 1;
`;

const taskAccessPredicate = `(${taskSourceAccessPredicate} AND (
  t.CreatedByUserId = dbo.fnGetContextUser()
  OR EXISTS (
    SELECT 1
    FROM dbo.TaskAssignees accessAssignee
    WHERE accessAssignee.TaskId = t.Id
      AND accessAssignee.UserId = dbo.fnGetContextUser()
  )
))`;

const taskListWhere = `
  WHERE ${taskAccessPredicate}
    AND (@accountId IS NULL OR t.AccountId = @accountId)
    AND (@contactId IS NULL OR t.ContactId = @contactId)
    AND (@noteId IS NULL OR t.SourceNoteId = @noteId)
    AND (@assigneeId IS NULL OR EXISTS (
      SELECT 1
      FROM dbo.TaskAssignees filterAssignee
      WHERE filterAssignee.TaskId = t.Id
        AND filterAssignee.UserId = @assigneeId
    ))
    AND (@status IS NULL OR t.Status = @status)
    AND (
      (@deletedOnly = 1 AND t.DeletedAt IS NOT NULL)
      OR (@deletedOnly = 0 AND (@includeDeleted = 1 OR t.DeletedAt IS NULL))
    )
`;

const taskOrder = `
  ORDER BY
    CASE WHEN t.Status IN ('Done', 'Canceled') THEN 1 ELSE 0 END,
    CASE WHEN t.DueDate IS NULL THEN 1 ELSE 0 END,
    t.DueDate,
    t.UpdatedAt DESC
`;

const taskRoutes: DataRoutes = {
  'get /api/data/tasks': {
    cache: false,
    schema: z.object({
      accountId: z.number().int().nullable().default(null),
      contactId: z.number().int().nullable().default(null),
      noteId: z.number().int().nullable().default(null),
      assigneeId: z.number().int().nullable().default(null),
      status: taskStatusSchema.nullable().default(null),
      includeDeleted: z.boolean().default(false),
      deletedOnly: z.boolean().default(false),
    }),
    query: `${taskSelect}
      ${taskListWhere}
      ${taskOrder}`,
  },

  'get /api/data/tasks/:id': {
    cache: false,
    schema: z.object({
      id: z.number().int(),
    }),
    queryOne: `${taskSelect}
      WHERE t.Id = @id
        AND ${taskAccessPredicate}
        AND t.DeletedAt IS NULL`,
  },

  'get /api/data/accounts/:id/tasks': {
    cache: false,
    schema: z.object({
      id: z.number().int(),
    }),
    query: `${taskSelect}
      WHERE t.AccountId = @id
        AND ${taskAccessPredicate}
        AND t.DeletedAt IS NULL
      ${taskOrder}`,
  },

  'get /api/data/contacts/:id/tasks': {
    cache: false,
    schema: z.object({
      id: z.number().int(),
    }),
    query: `${taskSelect}
      WHERE t.ContactId = @id
        AND ${taskAccessPredicate}
        AND t.DeletedAt IS NULL
      ${taskOrder}`,
  },

  'get /api/data/notes/:noteId/tasks': {
    cache: false,
    schema: z.object({
      noteId: z.number().int(),
    }),
    query: `${taskSelect}
      WHERE t.SourceNoteId = @noteId
        AND ${taskAccessPredicate}
        AND t.DeletedAt IS NULL
      ${taskOrder}`,
  },

  'post /api/data/tasks': {
    schema: z.object({
      title: z.string().trim().min(1).max(255),
      description: z.string().nullable().default(''),
      accountId: z.number().int(),
      contactId: z.number().int().nullable().default(null),
      sourceNoteId: z.number().int().nullable().default(null),
      dueDate: z.string().nullable().default(null),
      status: taskStatusSchema.default('Open'),
      priority: taskPrioritySchema.default('Medium'),
      sourceType: taskSourceTypeSchema.default('Manual'),
      assigneeIds: z.string().default(''),
      sendEmail: z.boolean().default(true),
    }),
    serverParams: ({ taskAssignmentEmail }) => ({
      notificationFromEmail: taskAssignmentEmail.from,
      notificationSubject: taskAssignmentEmail.subject,
      notificationBody: taskAssignmentEmail.body,
    }),
    queryOne: `
      SET NOCOUNT ON;
      SET XACT_ABORT ON;

      BEGIN TRY
      BEGIN TRANSACTION;

      DECLARE @now DATETIME2 = SYSDATETIME();
      DECLARE @notificationQueued INT = 0;
      DECLARE @notificationWarning NVARCHAR(255) = NULL;
      DECLARE @userId INT = dbo.fnGetContextUser();
      SELECT @userId = Id FROM dbo.Users WITH (HOLDLOCK) WHERE Id = @userId;
      IF ISNULL(@userId, 0) = 0 THROW 99101, 'Task create failed: user not found.', 1;

      IF NOT EXISTS (SELECT 1 FROM dbo.Accounts WHERE Id = @accountId)
        THROW 99102, 'Task create failed: account not found.', 1;

      IF @contactId IS NOT NULL AND NOT EXISTS (SELECT 1 FROM dbo.Contacts WHERE Id = @contactId)
        THROW 99103, 'Task create failed: contact not found.', 1;

      IF @sourceNoteId IS NOT NULL AND NOT EXISTS (SELECT 1 FROM dbo.Notes SourceCheck WITH (HOLDLOCK)
        JOIN dbo.fnVisibleNoteIds(@userId) VisibleSource ON VisibleSource.Id = SourceCheck.Id
        WHERE SourceCheck.Id = @sourceNoteId)
        THROW 99104, 'Task create failed: source note unavailable.', 1;

      DECLARE @assigneeIdsTable TABLE (Id INT PRIMARY KEY);
      INSERT INTO @assigneeIdsTable (Id)
      SELECT DISTINCT parsed.Id
      FROM (
        SELECT TRY_CAST(LTRIM(RTRIM(value)) AS INT) AS Id
        FROM STRING_SPLIT(@assigneeIds, ',')
      ) parsed
      WHERE parsed.Id IS NOT NULL
        AND parsed.Id > 0
        AND EXISTS (SELECT 1 FROM dbo.Users u WHERE u.Id = parsed.Id);

      IF NOT EXISTS (SELECT 1 FROM @assigneeIdsTable)
      BEGIN
        INSERT INTO @assigneeIdsTable (Id) VALUES (@userId);
      END

      DECLARE @effectiveSourceNoteId INT = @sourceNoteId;
      ${taskAssigneeLevelGuard}

      DECLARE @taskId INT;
      INSERT INTO dbo.Tasks (
        AccountId,
        ContactId,
        SourceNoteId,
        Title,
        Description,
        Status,
        Priority,
        DueDate,
        SourceType,
        CreatedByUserId,
        CreatedAt,
        UpdatedByUserId,
        UpdatedAt,
        CompletedAt
      )
      VALUES (
        @accountId,
        @contactId,
        @sourceNoteId,
        @title,
        NULLIF(@description, ''),
        @status,
        @priority,
        @dueDate,
        @sourceType,
        @userId,
        @now,
        @userId,
        @now,
        CASE WHEN @status = 'Done' THEN @now ELSE NULL END
      );

      SET @taskId = SCOPE_IDENTITY();

      INSERT INTO dbo.TaskAssignees (
        TaskId,
        UserId,
        CreatedBy,
        CreatedAt,
        UpdatedBy,
        UpdatedAt
      )
      SELECT
        @taskId,
        assignee.Id,
        @userId,
        @now,
        @userId,
        @now
      FROM @assigneeIdsTable assignee;

      COMMIT TRANSACTION;
      END TRY
      BEGIN CATCH
        IF XACT_STATE() <> 0 ROLLBACK TRANSACTION;
        THROW;
      END CATCH

      IF @sendEmail = 1
      BEGIN
        IF NULLIF(LTRIM(RTRIM(@notificationFromEmail)), '') IS NULL
        BEGIN
          SET @notificationWarning = 'Task created, but email notification could not be queued.';
        END
        ELSE
        BEGIN
          BEGIN TRY
            DECLARE @notificationRecipients TABLE (
              NormalizedEmail NVARCHAR(320) PRIMARY KEY,
              Email NVARCHAR(320) NOT NULL,
              AssigneeName NVARCHAR(255) NOT NULL
            );
            DECLARE @notificationResults TABLE (Id INT);
            DECLARE @normalizedRecipientEmail NVARCHAR(320);
            DECLARE @recipientEmail NVARCHAR(320);
            DECLARE @recipientName NVARCHAR(255);
            DECLARE @emailTaskTitle NVARCHAR(255);
            DECLARE @emailRelatedEntityName NVARCHAR(255);
            DECLARE @emailPriority NVARCHAR(50);
            DECLARE @emailDueDate NVARCHAR(10);
            DECLARE @emailTaskNotes NVARCHAR(MAX);
            DECLARE @emailTaskTitleHtml NVARCHAR(MAX);
            DECLARE @emailRelatedEntityNameHtml NVARCHAR(MAX);
            DECLARE @emailPriorityHtml NVARCHAR(MAX);
            DECLARE @emailDueDateHtml NVARCHAR(MAX);
            DECLARE @emailTaskNotesHtml NVARCHAR(MAX);
            DECLARE @recipientNameHtml NVARCHAR(MAX);
            DECLARE @personalizedNotificationSubject NVARCHAR(MAX);
            DECLARE @personalizedNotificationBody NVARCHAR(MAX);

            BEGIN TRANSACTION;

            SELECT
              @emailTaskTitle = REPLACE(CASE
                WHEN NULLIF(t.Description, N'') IS NULL THEN t.Title
                WHEN CHARINDEX(CHAR(10), t.Description) > 0
                  THEN LEFT(t.Description, CHARINDEX(CHAR(10), t.Description) - 1)
                ELSE t.Description
              END, CHAR(13), N''),
              @emailRelatedEntityName = COALESCE(c.Name, a.Name),
              @emailPriority = t.Priority,
              @emailDueDate = CONVERT(NVARCHAR(10), t.DueDate, 23),
              @emailTaskNotes = CASE
                WHEN CHARINDEX(CHAR(10), COALESCE(t.Description, N'')) > 0
                  THEN SUBSTRING(t.Description, CHARINDEX(CHAR(10), t.Description) + 1, 2147483647)
                ELSE N''
              END
            FROM dbo.Tasks t WITH (HOLDLOCK)
            INNER JOIN dbo.Accounts a ON a.Id = t.AccountId
            LEFT JOIN dbo.Contacts c ON c.Id = t.ContactId
            WHERE t.Id = @taskId;

            SET @emailTaskTitleHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailTaskTitle, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailRelatedEntityNameHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailRelatedEntityName, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailPriorityHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailPriority, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailDueDateHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailDueDate, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailTaskNotesHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailTaskNotes, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailTaskNotesHtml = REPLACE(REPLACE(REPLACE(@emailTaskNotesHtml, CHAR(13) + CHAR(10), N'<br>'), CHAR(10), N'<br>'), CHAR(13), N'<br>');

            INSERT INTO @notificationRecipients (NormalizedEmail, Email, AssigneeName)
            SELECT
              LOWER(LTRIM(RTRIM(u.Email))),
              MIN(LTRIM(RTRIM(u.Email))),
              COALESCE(MIN(NULLIF(LTRIM(RTRIM(u.Name)), N'')), N'There')
            FROM @assigneeIdsTable assignee
            INNER JOIN dbo.Users u WITH (HOLDLOCK) ON u.Id = assignee.Id
            WHERE NULLIF(LTRIM(RTRIM(u.Email)), '') IS NOT NULL
              AND NOT EXISTS (
                SELECT 1 FROM dbo.Tasks EmailTask
                JOIN dbo.Notes EmailSource WITH (HOLDLOCK) ON EmailSource.Id = EmailTask.SourceNoteId
                WHERE EmailTask.Id = @taskId AND EmailSource.VisibilityMode = 'levels'
                  AND u.AccessLevel < EmailSource.MinimumLevel
                  AND u.Id <> EmailSource.CreatedBy
              )
            GROUP BY LOWER(LTRIM(RTRIM(u.Email)));

            WHILE EXISTS (SELECT 1 FROM @notificationRecipients)
            BEGIN
              SELECT TOP (1)
                @normalizedRecipientEmail = recipient.NormalizedEmail,
                @recipientEmail = recipient.Email,
                @recipientName = recipient.AssigneeName
              FROM @notificationRecipients recipient
              ORDER BY recipient.NormalizedEmail;

              SET @recipientNameHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@recipientName, N'There'), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
              SET @personalizedNotificationSubject = REPLACE(
                @notificationSubject,
                N'{{taskTitle}}',
                COALESCE(REPLACE(REPLACE(@emailTaskTitle, CHAR(13), N' '), CHAR(10), N' '), N'')
              );
              SET @personalizedNotificationBody = REPLACE(CAST(@notificationBody AS NVARCHAR(MAX)), N'{{assigneeName}}', @recipientNameHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{taskTitle}}', @emailTaskTitleHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{relatedEntityName}}', @emailRelatedEntityNameHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{priority}}', @emailPriorityHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{dueDate}}', @emailDueDateHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{taskNotes}}', @emailTaskNotesHtml);

              DELETE FROM @notificationResults;
              INSERT INTO @notificationResults (Id)
              EXEC [Notifications].[dbo].[Create_Notification]
                @type = N'Email',
                @subject = @personalizedNotificationSubject,
                @body = @personalizedNotificationBody,
                @from = @notificationFromEmail,
                @to = @recipientEmail,
                @CC = N'',
                @bcc = N'';

              IF NOT EXISTS (SELECT 1 FROM @notificationResults)
                THROW 99105, 'Task notification queue did not return an ID.', 1;

              SET @notificationQueued += 1;
              DELETE FROM @notificationRecipients
              WHERE NormalizedEmail = @normalizedRecipientEmail;
            END

            COMMIT TRANSACTION;
          END TRY
          BEGIN CATCH
            IF XACT_STATE() <> 0 ROLLBACK TRANSACTION;
            SET @notificationQueued = 0;
            SET @notificationWarning = 'Task created, but email notification could not be queued.';
          END CATCH
        END
      END

      SELECT
        taskResult.*,
        CAST(@sendEmail AS BIT) AS notificationRequested,
        @notificationQueued AS notificationQueued,
        @notificationWarning AS notificationWarning
      FROM (
        ${taskSelect}
        WHERE t.Id = @taskId
          AND ${taskAccessPredicate}
      ) taskResult
    `,
  },

  'patch /api/data/tasks/:id': {
    schema: z.object({
      id: z.number().int(),
      title: z.string().trim().min(1).max(255).nullable().default(null),
      description: z.string().nullable().default(null),
      accountId: z.number().int().nullable().default(null),
      contactId: z.number().int().nullable().default(null),
      sourceNoteId: z.number().int().nullable().default(null),
      dueDate: z.string().nullable().default(null),
      status: taskStatusSchema.nullable().default(null),
      priority: taskPrioritySchema.nullable().default(null),
      assigneeIds: z.string().default(''),
      clearContact: z.boolean().default(false),
      clearSourceNote: z.boolean().default(false),
      clearDueDate: z.boolean().default(false),
      replaceAssignees: z.boolean().default(false),
      sendEmail: z.boolean().default(false),
    }),
    serverParams: ({ taskAssignmentEmail }) => ({
      notificationFromEmail: taskAssignmentEmail.from,
      notificationSubject: taskAssignmentEmail.subject,
      notificationBody: taskAssignmentEmail.body,
    }),
    queryOne: `
      SET NOCOUNT ON;
      SET XACT_ABORT ON;

      BEGIN TRY
      BEGIN TRANSACTION;

      DECLARE @now DATETIME2 = SYSDATETIME();
      DECLARE @notificationQueued INT = 0;
      DECLARE @notificationWarning NVARCHAR(255) = NULL;
      DECLARE @userId INT = dbo.fnGetContextUser();
      SELECT @userId = Id FROM dbo.Users WITH (HOLDLOCK) WHERE Id = @userId;
      IF ISNULL(@userId, 0) = 0 THROW 99111, 'Task update failed: user not found.', 1;

      IF NOT EXISTS (
        SELECT 1
        FROM dbo.Tasks t WITH (UPDLOCK, HOLDLOCK)
        LEFT JOIN dbo.TaskAssignees ta WITH (HOLDLOCK) ON ta.TaskId = t.Id
        WHERE t.Id = @id
          AND t.DeletedAt IS NULL
          AND (t.CreatedByUserId = @userId OR ta.UserId = @userId)
          AND ${taskSourceAccessPredicate}
      )
        THROW 99112, 'Task update failed: access denied.', 1;

      IF EXISTS (
        SELECT 1
        FROM dbo.Tasks ProtectedTask WITH (UPDLOCK, HOLDLOCK)
        JOIN dbo.Notes ProtectedSource WITH (HOLDLOCK) ON ProtectedSource.Id = ProtectedTask.SourceNoteId
        WHERE ProtectedTask.Id = @id
          AND ProtectedSource.VisibilityMode = 'levels'
          AND (@clearSourceNote = 1 OR (@sourceNoteId IS NOT NULL AND @sourceNoteId <> ProtectedTask.SourceNoteId))
      )
        THROW 99117, 'Forbidden: a task derived from a level note must keep its source note.', 1;

      IF @accountId IS NOT NULL AND NOT EXISTS (SELECT 1 FROM dbo.Accounts WHERE Id = @accountId)
        THROW 99113, 'Task update failed: account not found.', 1;

      IF @contactId IS NOT NULL AND NOT EXISTS (SELECT 1 FROM dbo.Contacts WHERE Id = @contactId)
        THROW 99114, 'Task update failed: contact not found.', 1;

      IF @sourceNoteId IS NOT NULL AND NOT EXISTS (SELECT 1 FROM dbo.Notes SourceCheck WITH (HOLDLOCK)
        JOIN dbo.fnVisibleNoteIds(@userId) VisibleSource ON VisibleSource.Id = SourceCheck.Id
        WHERE SourceCheck.Id = @sourceNoteId)
        THROW 99115, 'Task update failed: source note unavailable.', 1;

      DECLARE @effectiveSourceNoteId INT = (
        SELECT CASE WHEN @clearSourceNote = 1 THEN NULL
          ELSE COALESCE(@sourceNoteId, SourceNoteId) END
        FROM dbo.Tasks WHERE Id = @id
      );
      DECLARE @assigneeIdsTable TABLE (Id INT PRIMARY KEY);
      IF @replaceAssignees = 1
      BEGIN
        INSERT INTO @assigneeIdsTable (Id)
        SELECT DISTINCT parsed.Id
        FROM (
          SELECT TRY_CAST(LTRIM(RTRIM(value)) AS INT) AS Id
          FROM STRING_SPLIT(@assigneeIds, ',')
        ) parsed
        WHERE parsed.Id IS NOT NULL AND parsed.Id > 0
          AND EXISTS (SELECT 1 FROM dbo.Users u WHERE u.Id = parsed.Id);
        IF NOT EXISTS (SELECT 1 FROM @assigneeIdsTable)
          INSERT INTO @assigneeIdsTable (Id) VALUES (@userId);
      END
      ELSE
        INSERT INTO @assigneeIdsTable (Id)
        SELECT UserId FROM dbo.TaskAssignees WHERE TaskId = @id;
      ${taskAssigneeLevelGuard}

      UPDATE dbo.Tasks
      SET
        Title = COALESCE(@title, Title),
        Description = CASE WHEN @description IS NULL THEN Description ELSE NULLIF(@description, '') END,
        AccountId = COALESCE(@accountId, AccountId),
        ContactId = CASE WHEN @clearContact = 1 THEN NULL WHEN @contactId IS NOT NULL THEN @contactId ELSE ContactId END,
        SourceNoteId = CASE WHEN @clearSourceNote = 1 THEN NULL WHEN @sourceNoteId IS NOT NULL THEN @sourceNoteId ELSE SourceNoteId END,
        DueDate = CASE WHEN @clearDueDate = 1 THEN NULL WHEN @dueDate IS NOT NULL THEN @dueDate ELSE DueDate END,
        Status = COALESCE(@status, Status),
        Priority = COALESCE(@priority, Priority),
        UpdatedByUserId = @userId,
        UpdatedAt = @now,
        CompletedAt = CASE
          WHEN COALESCE(@status, Status) = 'Done' THEN COALESCE(CompletedAt, @now)
          WHEN @status IS NOT NULL AND @status <> 'Done' THEN NULL
          ELSE CompletedAt
        END
      WHERE Id = @id
        AND DeletedAt IS NULL;

      IF @replaceAssignees = 1
      BEGIN
        DELETE FROM dbo.TaskAssignees WHERE TaskId = @id;

        INSERT INTO dbo.TaskAssignees (TaskId, UserId, CreatedBy, CreatedAt, UpdatedBy, UpdatedAt)
        SELECT @id, assignee.Id, @userId, @now, @userId, @now
        FROM @assigneeIdsTable assignee;
      END

      COMMIT TRANSACTION;
      END TRY
      BEGIN CATCH
        IF XACT_STATE() <> 0 ROLLBACK TRANSACTION;
        THROW;
      END CATCH

      IF @sendEmail = 1
      BEGIN
        IF NULLIF(LTRIM(RTRIM(@notificationFromEmail)), '') IS NULL
        BEGIN
          SET @notificationWarning = 'Task updated, but email notification could not be queued.';
        END
        ELSE
        BEGIN
          BEGIN TRY
            DECLARE @notificationRecipients TABLE (
              NormalizedEmail NVARCHAR(320) PRIMARY KEY,
              Email NVARCHAR(320) NOT NULL,
              AssigneeName NVARCHAR(255) NOT NULL
            );
            DECLARE @notificationResults TABLE (Id INT);
            DECLARE @normalizedRecipientEmail NVARCHAR(320);
            DECLARE @recipientEmail NVARCHAR(320);
            DECLARE @recipientName NVARCHAR(255);
            DECLARE @emailTaskTitle NVARCHAR(255);
            DECLARE @emailRelatedEntityName NVARCHAR(255);
            DECLARE @emailPriority NVARCHAR(50);
            DECLARE @emailDueDate NVARCHAR(10);
            DECLARE @emailTaskNotes NVARCHAR(MAX);
            DECLARE @emailTaskTitleHtml NVARCHAR(MAX);
            DECLARE @emailRelatedEntityNameHtml NVARCHAR(MAX);
            DECLARE @emailPriorityHtml NVARCHAR(MAX);
            DECLARE @emailDueDateHtml NVARCHAR(MAX);
            DECLARE @emailTaskNotesHtml NVARCHAR(MAX);
            DECLARE @recipientNameHtml NVARCHAR(MAX);
            DECLARE @personalizedNotificationSubject NVARCHAR(MAX);
            DECLARE @personalizedNotificationBody NVARCHAR(MAX);

            BEGIN TRANSACTION;

            SELECT
              @emailTaskTitle = REPLACE(CASE
                WHEN NULLIF(t.Description, N'') IS NULL THEN t.Title
                WHEN CHARINDEX(CHAR(10), t.Description) > 0
                  THEN LEFT(t.Description, CHARINDEX(CHAR(10), t.Description) - 1)
                ELSE t.Description
              END, CHAR(13), N''),
              @emailRelatedEntityName = COALESCE(c.Name, a.Name),
              @emailPriority = t.Priority,
              @emailDueDate = CONVERT(NVARCHAR(10), t.DueDate, 23),
              @emailTaskNotes = CASE
                WHEN CHARINDEX(CHAR(10), COALESCE(t.Description, N'')) > 0
                  THEN SUBSTRING(t.Description, CHARINDEX(CHAR(10), t.Description) + 1, 2147483647)
                ELSE N''
              END
            FROM dbo.Tasks t WITH (HOLDLOCK)
            INNER JOIN dbo.Accounts a ON a.Id = t.AccountId
            LEFT JOIN dbo.Contacts c ON c.Id = t.ContactId
            WHERE t.Id = @id;

            SET @emailTaskTitleHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailTaskTitle, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailRelatedEntityNameHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailRelatedEntityName, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailPriorityHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailPriority, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailDueDateHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailDueDate, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailTaskNotesHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@emailTaskNotes, N''), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
            SET @emailTaskNotesHtml = REPLACE(REPLACE(REPLACE(@emailTaskNotesHtml, CHAR(13) + CHAR(10), N'<br>'), CHAR(10), N'<br>'), CHAR(13), N'<br>');

            INSERT INTO @notificationRecipients (NormalizedEmail, Email, AssigneeName)
            SELECT
              LOWER(LTRIM(RTRIM(u.Email))),
              MIN(LTRIM(RTRIM(u.Email))),
              COALESCE(MIN(NULLIF(LTRIM(RTRIM(u.Name)), N'')), N'There')
            FROM dbo.TaskAssignees ta
            INNER JOIN dbo.Users u WITH (HOLDLOCK) ON u.Id = ta.UserId
            WHERE ta.TaskId = @id
              AND NULLIF(LTRIM(RTRIM(u.Email)), '') IS NOT NULL
              AND NOT EXISTS (
                SELECT 1 FROM dbo.Tasks EmailTask
                JOIN dbo.Notes EmailSource WITH (HOLDLOCK) ON EmailSource.Id = EmailTask.SourceNoteId
                WHERE EmailTask.Id = @id AND EmailSource.VisibilityMode = 'levels'
                  AND u.AccessLevel < EmailSource.MinimumLevel
                  AND u.Id <> EmailSource.CreatedBy
              )
            GROUP BY LOWER(LTRIM(RTRIM(u.Email)));

            WHILE EXISTS (SELECT 1 FROM @notificationRecipients)
            BEGIN
              SELECT TOP (1)
                @normalizedRecipientEmail = recipient.NormalizedEmail,
                @recipientEmail = recipient.Email,
                @recipientName = recipient.AssigneeName
              FROM @notificationRecipients recipient
              ORDER BY recipient.NormalizedEmail;

              SET @recipientNameHtml = REPLACE(REPLACE(REPLACE(REPLACE(COALESCE(@recipientName, N'There'), N'&', N'&amp;'), N'<', N'&lt;'), N'>', N'&gt;'), N'"', N'&quot;');
              SET @personalizedNotificationSubject = REPLACE(
                @notificationSubject,
                N'{{taskTitle}}',
                COALESCE(REPLACE(REPLACE(@emailTaskTitle, CHAR(13), N' '), CHAR(10), N' '), N'')
              );
              SET @personalizedNotificationBody = REPLACE(CAST(@notificationBody AS NVARCHAR(MAX)), N'{{assigneeName}}', @recipientNameHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{taskTitle}}', @emailTaskTitleHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{relatedEntityName}}', @emailRelatedEntityNameHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{priority}}', @emailPriorityHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{dueDate}}', @emailDueDateHtml);
              SET @personalizedNotificationBody = REPLACE(@personalizedNotificationBody, N'{{taskNotes}}', @emailTaskNotesHtml);

              DELETE FROM @notificationResults;
              INSERT INTO @notificationResults (Id)
              EXEC [Notifications].[dbo].[Create_Notification]
                @type = N'Email',
                @subject = @personalizedNotificationSubject,
                @body = @personalizedNotificationBody,
                @from = @notificationFromEmail,
                @to = @recipientEmail,
                @CC = N'',
                @bcc = N'';

              IF NOT EXISTS (SELECT 1 FROM @notificationResults)
                THROW 99116, 'Task notification queue did not return an ID.', 1;

              SET @notificationQueued += 1;
              DELETE FROM @notificationRecipients
              WHERE NormalizedEmail = @normalizedRecipientEmail;
            END

            COMMIT TRANSACTION;
          END TRY
          BEGIN CATCH
            IF XACT_STATE() <> 0 ROLLBACK TRANSACTION;
            SET @notificationQueued = 0;
            SET @notificationWarning = 'Task updated, but email notification could not be queued.';
          END CATCH
        END
      END

      SELECT
        taskResult.*,
        CAST(@sendEmail AS BIT) AS notificationRequested,
        @notificationQueued AS notificationQueued,
        @notificationWarning AS notificationWarning
      FROM (
        ${taskSelect}
        WHERE t.Id = @id
          AND ${taskSourceAccessPredicate}
      ) taskResult
    `,
  },

  'patch /api/data/tasks/:id/status': {
    schema: z.object({
      id: z.number().int(),
      status: taskStatusSchema,
    }),
    queryOne: `
      SET NOCOUNT ON;
      SET XACT_ABORT ON;

      BEGIN TRY
      BEGIN TRANSACTION;

      DECLARE @now DATETIME2 = SYSDATETIME();
      DECLARE @userId INT = dbo.fnGetContextUser();
      SELECT @userId = Id FROM dbo.Users WITH (HOLDLOCK) WHERE Id = @userId;
      IF ISNULL(@userId, 0) = 0 THROW 99121, 'Task status update failed: user not found.', 1;

      IF NOT EXISTS (
        SELECT 1
        FROM dbo.Tasks t WITH (UPDLOCK, HOLDLOCK)
        LEFT JOIN dbo.TaskAssignees ta WITH (HOLDLOCK) ON ta.TaskId = t.Id
        WHERE t.Id = @id
          AND t.DeletedAt IS NULL
          AND (t.CreatedByUserId = @userId OR ta.UserId = @userId)
          AND ${taskSourceAccessPredicate}
      )
        THROW 99122, 'Task status update failed: access denied.', 1;

      UPDATE dbo.Tasks
      SET
        Status = @status,
        UpdatedByUserId = @userId,
        UpdatedAt = @now,
        CompletedAt = CASE WHEN @status = 'Done' THEN COALESCE(CompletedAt, @now) ELSE NULL END
      WHERE Id = @id
        AND DeletedAt IS NULL;

      COMMIT TRANSACTION;
      END TRY
      BEGIN CATCH
        IF XACT_STATE() <> 0 ROLLBACK TRANSACTION;
        THROW;
      END CATCH

      ${taskSelect}
      WHERE t.Id = @id
        AND ${taskSourceAccessPredicate}
    `,
  },

  'delete /api/data/tasks/:id': {
    schema: z.object({
      id: z.number().int(),
    }),
    query: `
      DECLARE @userId INT = dbo.fnGetContextUser();
      IF ISNULL(@userId, 0) = 0 THROW 99131, 'Task delete failed: user not found.', 1;

      DECLARE @now DATETIME2 = SYSDATETIME();

      UPDATE t
      SET
        DeletedAt = @now,
        DeletedByUserId = @userId,
        UpdatedByUserId = @userId,
        UpdatedAt = @now
      FROM dbo.Tasks t
      WHERE t.Id = @id
        AND ${taskSourceAccessPredicate}
        AND DeletedAt IS NULL
        AND (
          CreatedByUserId = @userId
          OR EXISTS (
            SELECT 1
            FROM dbo.TaskAssignees ta
            WHERE ta.TaskId = @id
              AND ta.UserId = @userId
          )
        )
    `,
  },
};

export default taskRoutes;
